import {
  ChangeEventHandler,
  DetailedHTMLProps,
  InputHTMLAttributes,
  PropsWithChildren,
} from "react";
import { useFormContext } from "react-hook-form";
import NumberFormat, {
  NumberFormatValues,
  SourceInfo,
} from "react-number-format";

import { useLanguage } from "~/contexts/providers/language";
import englishTranslations from "~/localization/en";
import frenchTranslations from "~/localization/fr";
import { CreateMemberInput } from "~/types/user/user.types";

import DatePicker from "./datePicker";
import styles from "./index.module.scss";

// returns the opt
type Options = Parameters<ReturnType<typeof useFormContext>["register"]>[1];

type InputProps = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;

type Props = {
  register: keyof CreateMemberInput;
  label: string; // for seo
  options?: Options;
  type?:
    | "text"
    | "checkbox"
    | "file"
    | "radio"
    | "password"
    | "date"
    | "select"
    | "textarea"
    | "formatted-number";
  fileAccept?: string[];
  // maxFileSize?: number;
  numberFormat?: string;
  selectOptions?: { value: string; text: string }[];
  placeholderRequiredStar?: boolean;
  fileUploadText?: string;
  handleFileUpload?: ChangeEventHandler<HTMLInputElement>;
  onValueChange?: (values: NumberFormatValues, sourceInfo: SourceInfo) => void;
} & InputProps;

const ApplicationInput = ({
  register,
  label,
  options,
  type = "text",
  fileAccept,
  numberFormat,
  selectOptions,
  required = false,
  placeholder = "",
  placeholderRequiredStar = true,
  min,
  max,
  size = 9,
  fileUploadText,
  handleFileUpload,
  onChange,
  onValueChange,
  ...remainingInputProps
}: Props) => {
  const { register: registration } = useFormContext();
  const { language } = useLanguage();
  const applyTexts =
    language === "En"
      ? englishTranslations.applyPage
      : frenchTranslations.applyPage;

  const registrationProps = registration(register, {
    ...options,
    required,
    min,
    max,
    onChange,
  });

  const placeHolderWithRequire = `${placeholder}${
    required && placeholderRequiredStar ? "*" : ""
  }`;

  const Wrapper = ({ children }: PropsWithChildren) =>
    type == "checkbox" ||
    type == "radio" ||
    type == "date" ||
    type == "file" ? (
      <div className={styles.checkboxContainer}>
        <label className={styles.applicationInputLabel}>{label}</label>
        {children}
      </div>
    ) : (
      <>
        <label className={styles.applicationInputLabel}>{label}</label>
        {children}
      </>
    );

  if (type == "select") {
    if (selectOptions == undefined) return null;
    const defVal = remainingInputProps.defaultValue ?? "$_DEFAULT_VALUE_$";
    return (
      <select
        {...registrationProps}
        className={styles.applicationInput}
        style={{ width: "fit-content" }}
        defaultValue={defVal}
        required={required}
      >
        <option value={defVal} disabled hidden>
          {placeholder}
        </option>
        {selectOptions.map((option, i) => (
          <option key={`${register}-select-option-${i}`} value={option.value}>
            {option.text}
          </option>
        ))}
      </select>
    );
  }

  if (type == "textarea") {
    return (
      <Wrapper>
        <textarea
          {...registrationProps}
          className={styles.applicationInput}
          placeholder={placeholder}
        />
      </Wrapper>
    );
  }

  if (type == "file")
    return (
      <Wrapper>
        <input {...registrationProps} style={{ display: "none" }} type="text" />
        <input
          placeholder={placeHolderWithRequire}
          className={styles.applicationInput}
          type="file"
          accept={fileAccept?.join(", ")}
          onChange={handleFileUpload}
        />
        <span>
          {fileUploadText ?? applyTexts.applicationForm.uploadFileText}
        </span>
      </Wrapper>
    );

  if (type == "formatted-number")
    return (
      <Wrapper>
        <NumberFormat
          {...registrationProps}
          className={styles.applicationInput}
          placeholder={placeholder}
          size={size}
          format={numberFormat}
          onValueChange={onValueChange}
        />
      </Wrapper>
    );

  if (type == "date")
    return <DatePicker registrationProps={registrationProps} label={label} />;

  return (
    <Wrapper>
      <input
        {...registrationProps}
        placeholder={placeHolderWithRequire}
        className={styles.applicationInput}
        type={type}
        size={size}
        autoComplete="off"
        {...remainingInputProps}
      />
      {type == "checkbox" && <span />}
      {type == "radio" && <span>{placeholder}</span>}
    </Wrapper>
  );
};

export default ApplicationInput;
